<template>
  <div class="slingshot-pro-viz">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$v₂ = v₁ + 2u$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 仿真演示区 -->
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>探测器速度 (v)</label>
            <span class="val">{{ currentVelocity.toFixed(1) }} <small>km/s</small></span>
          </div>
          <div class="hud-item orange">
            <label>行星轨道速度 (u)</label>
            <span class="val">{{ planetVelocity.toFixed(1) }} <small>km/s</small></span>
          </div>
        </div>

        <svg width="100%" height="240" viewBox="0 0 400 240" class="starfield">
          <!-- 静态星空背景 - 优化：使用预生成的坐标避免重绘闪烁 -->
          <circle v-for="(star, index) in starPositions" :key="'s'+index" 
                  :cx="star.x" :cy="star.y" 
                  :r="star.r" fill="#94a3b8" />

          <!-- 行星 (运动参考系) -->
          <g :transform="`translate(${planetPos.x}, ${planetPos.y})`">
            <circle r="30" fill="url(#planet-glow)" />
            <circle r="28" fill="#0f172a" />
            <circle r="25" fill="#f59e0b" opacity="0.8" />
            <text y="45" text-anchor="middle" fill="#f59e0b" font-size="10" font-weight="700">运动行星 (u)</text>
            
            <!-- 行星速度矢量 -->
            <line x1="0" y1="0" x2="-40" y2="0" stroke="#f59e0b" stroke-width="2" marker-end="url(#arrow-orange)" />
          </g>

          <!-- 探测器轨迹 -->
          <path :d="backgroundPath" fill="none" stroke="#334155" stroke-dasharray="4,4" />
          
          <!-- 实时进度轨迹 -->
          <path :d="activePath" fill="none" stroke="#22d3ee" stroke-width="2" />

          <!-- 探测器 -->
          <g :transform="`translate(${scPos.x}, ${scPos.y}) rotate(${scAngle})`">
            <path d="M -6,-4 L 8,0 L -6,4 Z" fill="#fff" />
            <path v-if="isRunning" d="M -6,0 L -12,0" stroke="#fbbf24" stroke-width="2">
              <animate attributeName="opacity" values="1;0;1" dur="0.1s" repeatCount="indefinite" />
            </path>
          </g>

          <defs>
            <radialGradient id="planet-glow">
              <stop offset="0%" stop-color="#fbbf24" stop-opacity="0.4" />
              <stop offset="100%" stop-color="#fbbf24" stop-opacity="0" />
            </radialGradient>
            <marker id="arrow-orange" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
              <path d="M0,0 L6,3 L0,6 Z" fill="#f59e0b" />
            </marker>
          </defs>
        </svg>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
        <div class="control-card-grid">
           <el-button type="primary" :disabled="isRunning" @click="startSimulation" icon="el-icon-video-play" class="action-btn">
             {{ isRunning ? '正在助推...' : '发射探测器' }}
           </el-button>
        </div>

        <div class="discovery-card-compact" v-latex>
          <i class="el-icon-info"></i>
          <p><strong>核心洞察：</strong>引力助推本质上是<strong>弹性碰撞</strong>的宏观应用。探测器进入行星引力范围后被“甩”出，在此过程中“窃取”了行星极微小的轨道动能。相对于太阳参考系，探测器的最终速度 v₂ ≈ v₁ + 2u，从而实现无燃料加速。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GravitationalSlingshotViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      isRunning: false,
      scPos: { x: -20, y: 180 },
      planetPos: { x: 300, y: 120 },
      currentVelocity: 15,
      planetVelocity: 12,
      pathPoints: [],
      startTime: 0,
      running: true,
      starPositions: Array.from({ length: 30 }, () => ({
        x: Math.random() * 400,
        y: Math.random() * 240,
        r: 0.5 + Math.random()
      }))
    }
  },
  computed: {
    backgroundPath() {
      // Bezier curve approximating the gravity bend
      return "M -20,180 Q 250,180 300,120 T 450,-50";
    },
    activePath() {
      if (this.pathPoints.length < 2) return "";
      return "M " + this.pathPoints.map(p => `${p.x},${p.y}`).join(" L ");
    },
    scAngle() {
      if (this.pathPoints.length < 2) return 0;
      const p1 = this.pathPoints[this.pathPoints.length - 2];
      const p2 = this.pathPoints[this.pathPoints.length - 1];
      return Math.atan2(p2.y - p1.y, p2.x - p1.x) * 180 / Math.PI;
    }
  },
  methods: {
    startSimulation() {
      if (this.isRunning) return;
      this.isRunning = true;
      this.pathPoints = [];
      this.planetPos = { x: 350, y: 120 };
      this.currentVelocity = 15;
      this.startTime = Date.now();
      this.animate();
    },
    animate() {
      if (!this.running || !this.isRunning) return;

      const elapsed = (Date.now() - this.startTime) / 1000;
      const duration = 2.5;
      const t = Math.min(1, elapsed / duration);

      // Path parametric eq (Simple approximation)
      let x, y;
      if (t < 0.4) {
        // Approach
        const p = t / 0.4;
        x = -20 + p * 200;
        y = 180;
        this.currentVelocity = 15;
      } else if (t < 0.7) {
        // Swing-by (The bend)
        const p = (t - 0.4) / 0.3;
        const angle = Math.PI + p * Math.PI * 0.6;
        const r = 60; // Impact param
        x = 240 + Math.cos(angle) * r + p * 100;
        y = 120 + Math.sin(angle) * r;
        this.currentVelocity = 15 + p * 24; // Visual speed increase
      } else {
        // Departure
        const p = (t - 0.7) / 0.3;
        x = 340 + p * 150;
        y = 60 - p * 120;
        this.currentVelocity = 39; // v + 2u approx
      }

      this.scPos = { x, y };
      this.pathPoints.push({ x, y });
      if (this.planetPos.x > 0) this.planetPos.x -= 0.5; // Planet moving left

      if (t < 1) {
        this.frameId = requestAnimationFrame(this.animate);
      } else {
        setTimeout(() => { this.isRunning = false; }, 1000);
      }
    }
  },
  beforeDestroy() {
    this.running = false;
    cancelAnimationFrame(this.frameId);
  }
}
</script>

<style scoped lang="scss">
.slingshot-pro-viz { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }

.viz-header-compact {
  display: flex; justify-content: space-between; align-items: center; padding: 0 4px;
  .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; }
  .formula-chip { background: #fef3c7; color: #d97706; border: 1px solid #fde68a; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; }
}

.simulation-stage {
  flex: 1; min-height: 240px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden;
  .hud-panel {
    position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10;
    .hud-item {
      background: rgba(15, 23, 42, 0.8); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px;
      label { display: block; font-size: 10px; color: #64748b; font-weight: 700; }
      .val { font-size: 14px; color: #f1f5f9; font-weight: 800; font-family: monospace; small { font-size: 10px; color: #94a3b8; } }
      &.cyan { border-left: 3px solid #06b6d4; }
      &.orange { border-left: 3px solid #f59e0b; }
    }
  }
}

.action-btn { width: 100%; font-weight: 700; height: 36px; border-radius: 10px; }

.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card-grid { padding: 8px; background: #ffffff; border-radius: 12px; border: 1px solid #e2e8f0; }

.discovery-card-compact {
  background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: center; gap: 10px;
  i { color: #f59e0b; font-size: 1.1rem; }
  p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } }
}
</style>
